import { Component, OnInit, OnDestroy, Output, EventEmitter, Input } from '@angular/core';

import { DialogService } from 'primeng/dynamicdialog';

import { DataService, DateService, HelperService, HTTPService, SocketService } from 'app/services';
import { serviceStatusEnum } from 'app/constants';
import { datetimeFormat } from 'app/constants/date-time-format';
import { ConfirmDismissDialogComponent } from 'app/modules/shared/components';

import { takeUntil } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-technician-timer',
  templateUrl: './technician-timer.component.html',
  styleUrls: ['./technician-timer.component.scss'],
})
export class TechnicianTimerComponent implements OnInit, OnDestroy {
  @Output() appSetIsTimeVisible = new EventEmitter();
  @Input('currentLanguage')
  set setCurrentLang(value: string) {
    this.currentLang = value;
    this.translate.use(this.currentLang);
    this.getLastestActiveService();
  }
  @Input('appsettings')
  set appsettings(data: any) {
    if (data) {
      this.isBillableHoursEnabled = data.isBillableHoursEnabled;
    }
  }

  isBillableHoursEnabled: boolean = false;
  costEstimatedHour: number = 0;
  service: any = {};
  timeAllocated: string = '0:00';
  timeSpent: string = '0:00';
  timeAllocatedMin: number = 0;
  timeSpentMin: number = 0;
  timeLogs: any[] = [];
  interval: any = '';
  isExpanded: boolean = false;
  greenProgressbarLength: number = 0;
  dataServiceListener: any = [];
  isProcessing: boolean = false;
  isStatusUpdating: boolean = false;
  isMarkingComplete: boolean = false;
  socketListener: Subject<boolean> = new Subject<boolean>();
  techId: any = '';
  isHideToolbar: boolean = false;
  showAtBottom: boolean = false;
  subscription: Subscription[] = []
  currentLang: string = 'en';
  constructor(
    private httpService: HTTPService,
    private helperService: HelperService,
    private dateService: DateService,
    private dataService: DataService,
    private dialogService: DialogService,
    private socketService: SocketService,
    private translate: TranslateService
  ) { }

  ngOnInit() {

    this.subscription.push(
      this.helperService.hideBottomDetailsInTechApp.subscribe(status => {
        this.showAtBottom = status;
      })
    )
    this.techId = this.helperService.getCompany().emplyeeId;
    this.getLastestActiveService();
    this.setDataListener();
    this.setSocketListener();
  }

  ngOnDestroy() {
    if (this.interval) {
      clearInterval(this.interval);
    }

    if (this.socketListener) {
      this.socketListener.next(true);
      this.socketListener.unsubscribe();
    }

    this.subscription.forEach(listener => listener.unsubscribe())
  }

  setDataListener() {
    this.dataServiceListener = this.dataService.getData().subscribe(({ data }) => {
      if (!data) {
        return;
      }
      const { key } = data;

      switch (key) {
        case 'unauthorized-service-success':
          const { serviceData } = data.data;

          if (serviceData && serviceData.fromTimer) {
            this.updateStatus({ ...serviceData.data, forceStart: true });
          }
          break;

        case 'clock-updated-resume-action':
          if (data.data) {
            const { serviceData } = data.data;

            if (serviceData) {
              this.updateStatus(serviceData.data, serviceData.isMarkComplete);
            }
          }
          break;

        case 'clock-updated-resume-action':
          this.isStatusUpdating = false;
          break;

        case 'refetch-active-service':
          if (/^iP/.test(navigator.platform)) {
            this.getLastestActiveService();
          }
          break;

        case 'refetch-billed-hour':
          this.getLastestActiveService();
          break;

        case 'trigger-service-status-update':
          if (data.data && data.data.triggerFrom === 'timer') {
            if (data.data.isMarkCompleted) {
              this.markComplete(true);
            } else {
              this.serviceAction(true);
            }
          }
          break;

        case 'trigger-vin-success':
          if (data.data && data.key === 'trigger-vin-success' && data.data.triggerFrom === 'timer') {
            this.updateStatus(data.data);
          }
          break;

        case 'show-toolbar':
          this.isHideToolbar = false;
          break;
        case 'hide-toolbar':
          this.isHideToolbar = true;
          break;

        default:
          break;
      }
    });
  }

  setSocketListener() {
    this.socketService
      .socketOn(`tech_${this.techId}`)
      .pipe(takeUntil(this.socketListener))
      .subscribe((data) => {
        switch (data.type) {
          case 'refetch-active-service':
            this.getLastestActiveService();
            break;

          case 'refetch-time-clock':
            this.dataService.sendData(this.dataService.typePassData('trigger-employee-resync', {}));
            break;

          default:
            break;
        }
      });
  }

  updateTimer() {
    const ahours = Math.floor(this.timeAllocatedMin / 60);
    const amins = Math.round(this.timeAllocatedMin % 60);
    const shours = Math.floor(this.timeSpentMin / 60);
    const smins = Math.round(this.timeSpentMin % 60);

    this.timeAllocated = `${ahours > 9 ? ahours : `0${ahours}`}:${amins > 9 ? amins : `0${amins}`}`;
    this.timeSpent = `${shours > 9 ? shours : `0${shours}`}:${smins > 9 ? smins : `0${smins}`}`;

    if (this.timeSpentMin > this.timeAllocatedMin) {
      this.greenProgressbarLength = 100 - Math.floor(100 * (this.timeAllocatedMin / this.timeSpentMin) * 100);
    } else {
      this.greenProgressbarLength = Math.floor(100 * (this.timeSpentMin / this.timeAllocatedMin));
    }
  }

  checkBeforeTriggerAction(isMarkCompleted: boolean = false, event?: Event, forcePaused: boolean = false) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const { tech, status, title } = this.service;

    this.dataService.sendData(
      this.dataService.typePassData('check-for-service-editable', {
        type: 'service',
        status,
        tech,
        forcePaused,
        isMarkCompleted,
        triggerFrom: 'timer',
        data: {
          techName: (tech && tech.name) || '',
          serviceTitle: title,
        },
      })
    );
  }

  getLastestActiveService() {
    this.isStatusUpdating = true;
    this.httpService.getActiveService().then(
      (res) => {
        if (res) {
          const { currentActiveService, timeLogs, totalTimeSpent } = res;
          if (this.interval) {
            clearInterval(this.interval);
          }

          if (currentActiveService && currentActiveService._id) {
            this.service = currentActiveService;
            this.timeSpentMin = totalTimeSpent || 0;

            if (timeLogs && timeLogs.length) {
              this.timeLogs = timeLogs;
              this.timeLogs.forEach((log: any) => {
                const name = `${log && log.employeeId && log.employeeId.firstName} ${log && log.employeeId && log.employeeId.lastName}`;

                log.tech = name;
                log.startTz = this.currentLang === 'fr' ? this.dateService.formatDate(new Date(log.start), datetimeFormat.mmmmdhhmma, 'fr') : this.dateService.formatDate(new Date(log.start), datetimeFormat.mmmdhhmma);

                if (log.end) {
                  log.endTz = this.currentLang === 'fr' ? this.dateService.formatDate(new Date(log.end), datetimeFormat.mmmmdhhmma, 'fr') : this.dateService.formatDate(new Date(log.end), datetimeFormat.mmmdhhmma);
                } else {
                  log.endTz = 'In progress';
                }
              });
            }

            if (this.service) {
              if (this.isBillableHoursEnabled && this.service.billableHours) {
                this.timeAllocatedMin = 0;
                this.timeAllocatedMin = this.service.billableHours * 60;
              } else if (!this.isBillableHoursEnabled) {
                if (this.service && this.service.items) {
                  this.costEstimatedHour = 0;
                  this.service.items.forEach((item) => {
                    if (item.type === 'labor') {
                      this.costEstimatedHour += item.costEstimatedHours;
                      this.timeAllocatedMin = this.costEstimatedHour * 60;
                      //ALP-8452 -- start fix -- Service time not showing for flat rate items on techapp
                    } else if (item.type === 'bundle') {
                      if (item.subItems) {
                        item.subItems.forEach((subItem: any) => {
                          if (subItem.type === 'labor') {
                            this.costEstimatedHour += subItem.costEstimatedHours;
                            this.timeAllocatedMin = this.costEstimatedHour * 60;
                          }
                        });
                      }
                    }
                    //ALP-8452 -- end fix -- Service time not showing for flat rate items on techapp
                  });
                }
              }

              if (this.service.timesheet) {
                this.updateTimer();

                if (this.service.timesheet && this.service.timesheet.action === 'inprogress') {
                  this.interval = setInterval(() => {
                    this.timeSpentMin += 1;
                    this.updateTimer();
                  }, 60000);
                }
              }
            }
            this.appSetIsTimeVisible.emit(true);
          } else {
            this.service = {};
            this.appSetIsTimeVisible.emit(false);
          }

          this.isStatusUpdating = false;
        }
      },
      (err) => {
        this.helperService.showToaster('error', err);
      }
    );
  }

  getTranslatedText(value: any) {
    if(value === 'In progress') {
      return this.currentLang === 'fr' ? 'En cours' : 'In progress';
    } else {
      return value;
    }
  }

  toggleExpand() {
    this.isExpanded = !this.isExpanded;
  }

  /** Marking the service complete */
  markComplete(forceClockIn: boolean = false, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.updateStatus(
      {
        forceClockIn,
        title: this.service.title,
        estimateId: this.service.orderId,
        workId: this.service._id,
        workType: 'service',
        status: 'completed',
      },
      true
    );
  }

  /** Update the status of the job only works for [inprogress, pause, new, completed] */
  serviceAction(forceClockIn: boolean = false, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (serviceStatusEnum.indexOf(this.service.status) !== -1) {
      const employeeId = this.helperService.getCompany().emplyeeId;
      const { tech, hTechOne, hTechTwo } = this.service;
      let techServiceStatus = '';
      if (tech && tech.techId === employeeId) {
        techServiceStatus = tech.workStatus || this.service.status;
      } else if (hTechOne && hTechOne.techId === employeeId) {
        techServiceStatus = hTechOne.workStatus;
      } else if (hTechTwo && hTechTwo.techId === employeeId) {
        techServiceStatus = hTechTwo.workStatus;
      } else {
        return;
      }
       const updateParams: any = {
        forceClockIn,
        title: this.service.title,
        estimateId: this.service.orderId,
        workId: this.service._id,
        workType: 'service',
        status: techServiceStatus === 'inprogress' ? 'pause' : 'inprogress',
      };
  
      if (updateParams.status === 'inprogress') {
        updateParams.forceStart = true;
      }
      this.updateStatus(updateParams);
    }
  }

  /** force fully start service by stop the current inprogress one */
  forceStart() {
    this.updateStatus({
      title: this.service.title,
      estimateId: this.service.orderId,
      workId: this.service._id,
      workType: 'service',
      override: true,
      status: 'inprogress',
    });
  }

  /** Confirmation dialog for start force fully start service by stop the current inprogress one */
  openConfirmForceStartDialog({ title, workType }) {
    const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
      width: '90%',
      styleClass: 'small-heading-dialog',
      showHeader: false,
      data: {
        title: 'Confirmation',
        message: `Are you sure you want to start this service, starting this will pause the "${title}" ${workType} which is in progress?`,
        button: {
          success: { title: 'Start' },
          cancel: { title: 'Cancel' },
        },
      },
    });
    const onCloseSub = ref.onClose.subscribe((confirm) => {
      if (confirm.isSuccess) {
        this.forceStart();
      } else {
        this.isProcessing = false;
        this.isStatusUpdating = false;
        this.isMarkingComplete = false;
      }
      onCloseSub.unsubscribe();
    });
  }

  /** Showing loaders and updating status */
  updateStatus(data: any, isMarkComplete?: any) {
    if ((this.isStatusUpdating || this.isMarkingComplete || this.isProcessing) && !data.override) {
      return;
    }

    data.fromTimer = true;
    this.isStatusUpdating = true;
    this.httpService.serviceAction(this.service.orderId || (data && data.estimateId), data).then(
      (res: any) => {

        /*This comment is made because the unauthorized dialog is opening twice 
         unnecessarily. There's no need to open it from here because the unauthorized
        dialog is opening from every component anyway.*/

        // if (res && res.isPendingService) {
        //   this.dataService.sendData(
        //     this.dataService.typePassData('unauthorized-service', {
        //       serviceData: { data, isMarkComplete, fromTimer: true },
        //     })
        //   );
        //   this.isProcessing = false;
        //   this.isStatusUpdating = false;
        //   this.isMarkingComplete = false;
        // } else
        if (res && res.msg === 'Already inprogress.') {
          this.openConfirmForceStartDialog(res.data || {});
        } else if (res && res.msg === 'clock-out') {
          const { tech, status, title } = this.service;

          this.dataService.sendData(
            this.dataService.typePassData('check-for-service-editable', {
              type: 'service',
              status,
              tech,
              triggerFrom: 'timer',
              data: {
                techName: (tech && tech.name) || '',
                serviceTitle: title,
              },
            })
          );
        } else if (res && res.msg === 'no-vin') {
          this.isProcessing = false;
          this.isStatusUpdating = false;
          this.isMarkingComplete = false;
          this.dataService.sendData(
            this.dataService.typePassData('no-vin-found', {
              triggerFrom: 'timer',
              payload: { data },
              vehicle: res.vehicle,
              customer: res.customer,
              required: res.required,
            })
          );
        } else if (data.forceClockIn) {
          this.dataService.sendData(
            this.dataService.typePassData('trigger-service=success-toastr', {
              pStatus: this.service.status,
              nStatus: data.status,
            })
          );
        }
        this.dataService.sendData(this.dataService.typePassData('refetch-active-service', {}));
        this.dataService.sendData(this.dataService.typePassData('trigger-employee-resync', {}));

        // this.isStatusUpdating = false;
      },
      (err) => {
        this.isStatusUpdating = false;
        this.helperService.showToaster('error', err);
      }
    );
  }
}
